import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import Button from '../components/Button';
import SectionTitle from '../components/SectionTitle';
import ServicesGrid from '../components/ServicesGrid';
import BadgesRow from '../components/BadgesRow';
import VideoEmbed from '../components/VideoEmbed';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';
import Lightbox from '../components/Lightbox';
import VideoHeroIframe from '../components/VideoHeroIframe';

// Import utilities
import { getStreamIframeUrl, getStreamThumbnailUrl } from '../lib/cloudflareStream';

// Import content
import siteContent from '../content/site.json';
import portfolioContent from '../content/portfolio.json';

// Get residential portfolio items for carousels
const residential2Item = portfolioContent.items.find(item => item.id === 're-austin-hillcountry-photo');
const residential3Item = portfolioContent.items.find(item => item.id === 're-austin-downtown-photo');

/**
 * Home page component
 * @returns {React.ReactElement} Home page
 */
const HomePage = () => {
  // Get content from JSON files
  const { hero: heroContent, services, badges } = siteContent;
  
  // State for video modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // State for lightbox/carousel
  const [selectedItem, setSelectedItem] = useState(null);
  
  // We're using custom portfolio items now instead of the dynamic ones
  // eslint-disable-next-line no-unused-vars
  const portfolioItems = portfolioContent.items.slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Helmet>
        <title>Professional Drone Services Across Texas | Lone Star Aerial Solutions</title>
        <meta name="description" content="FAA-certified aerial photography, videography & mapping services for real estate, construction & commercial clients across Austin, Houston, Dallas & Central Texas. Fast turnaround, fully insured. Get your free quote today!" />
        <meta name="keywords" content="drone services Austin Texas, aerial photography Austin TX, commercial drone videography Texas, construction site drone mapping, real estate aerial photography Austin Houston Dallas, FAA certified drone pilot Texas, drone inspections Austin" />
        <link rel="canonical" href="https://lonestaraerialsolutions.com/" />
        
        {/* Structured Data for Local Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Lone Star Aerial Solutions",
            "image": "https://lonestaraerialsolutions.com/assets/logo/logo-lsas-full.svg",
            "description": "Professional drone services across Texas including aerial photography, videography, mapping, and inspections for real estate and businesses.",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "TX",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 31.9686,
              "longitude": -99.9018
            },
            "url": "https://lonestaraerialsolutions.com",
            "telephone": "+1-737-777-8044",
            "priceRange": "$$",
            "serviceArea": {
              "@type": "State",
              "name": "Texas"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Drone Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Aerial Photography",
                    "description": "Professional aerial photography for real estate and commercial properties"
                  }
                },
                {
                  "@type": "Offer", 
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Aerial Videography",
                    "description": "4K aerial videography and cinematic drone footage"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service", 
                    "name": "Aerial Mapping",
                    "description": "Precision aerial mapping and surveying services"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative text-white w-full pt-16 md:pt-0 mb-8 sm:mb-12 md:mb-0">
        <VideoHeroIframe
          videoId="d672935539ebb0f9e5bd9f2e6ea5d782"
          fallbackImage={heroContent.heroImage}
          alt="Professional drone aerial photography Austin Texas landscapes and commercial properties"
          className="min-h-screen"
        >
          <div className="flex flex-col sm:justify-center pb-6 md:pb-0 md:pt-0 min-h-screen">
            {/* Spacer to position content halfway down */}
            <div className="sm:hidden" style={{ height: '40vh' }}></div>
            <Container>
              <div className="max-w-3xl text-left mb-4 sm:mb-0">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 drop-shadow-lg">
                  {heroContent.headline}
                </h1>
                <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 text-white drop-shadow-lg leading-snug">
                  {heroContent.subhead}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12 md:mb-0">
                  <Button href="/contact">
                    {heroContent.ctaLabel}
                  </Button>
                  <Button href="/portfolio" variant="secondary">
                    View Portfolio
                  </Button>
                </div>
              </div>
            </Container>
          </div>
        </VideoHeroIframe>
      </section>

      {/* Services Section */}
      <section className="pt-4 sm:pt-8 md:pt-6 pb-6 md:pb-6 bg-brand-black">
        <Container>
          <SectionTitle 
            title="OUR SERVICES" 
            className="text-white"
          />
          <ServicesGrid services={services} />
        </Container>
      </section>

      {/* Portfolio Teaser */}
      <section className="pt-6 pb-6 md:pt-6 md:pb-6 bg-brand-black">
        <Container>
          <SectionTitle 
            title="PORTFOLIO" 
            subtitle="A selection of our recent aerial photography and videography work"
            className="text-white"
          />
          
          {/* Portfolio Grid with custom items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* First Item - Using Residential2 image with carousel */}
            <div 
              className="rounded-2xl overflow-hidden shadow-card group cursor-pointer"
              onClick={() => setSelectedItem(residential2Item)}
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img 
                  src="/assets/portfolio/Residential2/1.png" 
                  alt="Aerial photography residential property Austin Texas real estate drone services" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Gallery indicator */}
                <div className="absolute top-2 right-2 bg-brand-blue-600 rounded-full p-2">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white font-medium px-4 py-2 rounded-lg bg-brand-navy/70">
                    View Gallery
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Residential Property in Austin</h3>
                <p className="text-sm text-gray-300 mt-1">Photo Gallery • Click to view all images</p>
              </div>
            </div>
            
            {/* Second Item - Using Residential1 image with video embed */}
            <div 
              className="rounded-2xl overflow-hidden shadow-card group cursor-pointer" 
              onClick={() => setShowVideoModal(true)}
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img 
                  src="/assets/portfolio/Residential1/1.png" 
                  alt="Commercial drone videography Austin Texas residential property aerial tour" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Video play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white/90 rounded-full p-3">
                    <svg className="w-8 h-8 text-brand-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
                {/* Video indicator text overlay */}
                <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors flex items-end justify-center opacity-0 group-hover:opacity-100 pb-4">
                  <span className="text-white font-medium px-4 py-2 rounded-lg bg-brand-navy/70">
                    Play Video
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Residential Property - Aerial Tour</h3>
                <p className="text-sm text-gray-300 mt-1">Video • Click to play</p>
              </div>
            </div>
            
            {/* Third Item - Using Residential3 image with carousel */}
            <div 
              className="rounded-2xl overflow-hidden shadow-card group cursor-pointer"
              onClick={() => setSelectedItem(residential3Item)}
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img 
                  src="/assets/portfolio/Residential3/1.png" 
                  alt="Austin Texas residential property aerial photography drone services real estate marketing" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Gallery indicator */}
                <div className="absolute top-2 right-2 bg-brand-blue-600 rounded-full p-2">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white font-medium px-4 py-2 rounded-lg bg-brand-navy/70">
                    View Gallery
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold">Residential Property in Austin</h3>
                <p className="text-sm text-gray-300 mt-1">Photo Gallery • Click to view all images</p>
              </div>
            </div>
          </div>
          
          {/* Video Modal */}
          {showVideoModal && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <div className="relative max-w-4xl w-full bg-black rounded-lg overflow-hidden">
                <button 
                  className="absolute top-4 right-4 text-white hover:text-brand-blue-600 z-10"
                  onClick={() => setShowVideoModal(false)}
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <ComponentErrorBoundary 
                  fallbackMessage="Video is temporarily unavailable."
                  componentName="VideoEmbed"
                >
                  <VideoEmbed 
                    src={getStreamIframeUrl('ed343f63a184cf9a0eb02884e1a76a77', {
                      poster: getStreamThumbnailUrl('ed343f63a184cf9a0eb02884e1a76a77', { height: 600 })
                    })}
                    title="Residential Property - Aerial Tour"
                  />
                </ComponentErrorBoundary>
              </div>
            </div>
          )}
          
          <div className="text-center">
            <Button href="/portfolio">View Full Portfolio</Button>
          </div>
        </Container>
      </section>

      {/* Badges Section */}
      <section className="py-12 bg-brand-black">
        <Container>
          <BadgesRow items={badges} />
        </Container>
      </section>

      <Footer />

      {/* Lightbox for portfolio carousels */}
      {selectedItem && (
        <Lightbox item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
};

export default HomePage;
